import { ObjectManager } from '../../../concepts/objects';
import { Rect } from '../../../platform/geom/Rect';
import { KeyInput } from '../../../platform/input/KeyInput';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { Camera } from '../../canvas/Camera';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { defineToolWithCommonBehaviors } from '../../canvas/tooling/Definitions';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { Graffiti, GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { resolve } from '../../Container';

class AbstractEraserToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(ObjectManager) private objects!: ObjectManager;

    private subject: Physical | null = null;

    constructor(private triggerButton: MouseButtons) {
        super();
    }

    public getPreferredCursor(state: string, xi: MouseInput | KeyInput) {
        if ((state === 'idle' && xi instanceof MouseInput && xi.button === this.triggerButton) || state === 'erasing') {
            const subject = xi.targetPhysical;
            if (!subject?.is(GraffitiTarget)) return;
            return 'eraser';
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {

        if (state !== 'idle' || mi.button !== this.triggerButton) {
            return;
        }

        const subject = mi.targetPhysical;
        if (!subject?.is(GraffitiTarget)) return;

        this.subject = subject;
        return 'erasing';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        const { camera, objects, subject } = this;

        if (state !== 'erasing' || !subject?.alive) {
            return;
        }

        const mpt = camera.toModelPoint(mi.position);
        if (subject.bounds(RelativeTo.Model).contains(mpt)) {

            const rpt = subject.toRelative(mpt);
            const hpts = Rect.fromCenter(rpt, 1).lines().flatMap(x => [x.p1, x.mid]);
            const hits = new Set<Graffiti>();

            for (const ch of subject.children) {
                const gr = ch.as(Graffiti);
                if (!gr?.alive) continue;
                const ol = gr.outline();
                if (!hpts.find(x => ol.contains(x))) continue;
                hits.add(gr);
            }

            if (hits.size) {
                objects.update(u => {
                    u.set('key', `erase:${subject.id}`);
                    u.set('rolling', true);
                    for (const h of hits) {
                        u.delete(h.removeSelf());
                    }
                });
            }
        }

    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        if (state === 'erasing' && mi.button === this.triggerButton) {
            this.objects.update(u => u.noop());
            return 'idle';
        }
    }
}

export const EraserToolBehavior = (button: MouseButtons) =>
    class extends AbstractEraserToolBehavior {
        constructor() {
            super(button);
        }
    };


export const eraserTool = defineToolWithCommonBehaviors({
    id: 'eraserTool',
    label: 'Eraser',
    category: 'misc',
    behaviors: [
        EraserToolBehavior(MouseButtons.Main),
    ]
});
